import { lazy, Suspense } from 'react';
import { useState, useEffect, useRef } from 'react';
import classes from './index.module.scss';
import { useAppSelector } from '../../hooks/useAppSelector';
import moment from 'moment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SubmitAssessmentDialog from '../SubmitAssessmentDialog';
import { useDispatch } from 'react-redux';
import { authStatusEnum, setStatus } from '../../store/auth/authSlice';
import { assessmentSubmission } from '../../services/api/assessment';
import { useParams } from 'react-router-dom';
import { CustomSnackbarProps } from '../CustomSnackbar';
import { setToastState } from '../../store/toast/toastSlice';
import { generalErrorMessage } from '../../constants/messages';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import {
  questionMap,
  setPhotoUploadPause,
  setQuestionsAnswered,
  setQuestionsMap,
} from '../../store/assessment/assessmentSlice';
import { NetworkStatusEnum } from '../../store/network/networkSlice';
import { ExitFullScreen } from '../../utils/helper';
import { tokenKey } from '../../utils/apiClient/contant';
import { setEndTime, setTimeRemain } from '../../store/time/timeSlice';
const NavMobile = lazy(() => import('../NavMobile'));

interface HeaderProps {
  isMobile: boolean;
}

const Header = ({ isMobile }: HeaderProps) => {
  const dispatch = useDispatch();
  const { accessKey } = useParams();
  const assessmentState = useAppSelector((state) => state.assessment);
  const { timeRemain, pauseSubmit, codeRunning, videoRecording, endTime } =
    useAppSelector((state) => state.time);
  const networkState = useAppSelector((state) => state.network);
  const toastState = useAppSelector((state) => state.toast);
  const [openSubmitPopup, setOpenSubmitPopup] = useState(false);
  const [timeClass, setTimeClass] = useState(classes.time);
  const [pause, setPause] = useState(false);
  const submitInFlight = useRef(false);

  const openSnackbar = (
    type: CustomSnackbarProps['type'],
    message: CustomSnackbarProps['message']
  ) => {
    dispatch(
      setToastState({
        ...toastState,
        type,
        open: true,
        message,
      })
    );
  };

  useEffect(() => {
    dispatch(setTimeRemain(assessmentState.time_remaining));
    if (assessmentState.time_remaining !== Infinity) {
      dispatch(setEndTime(Date.now() + assessmentState.time_remaining * 1000));
    }
  }, [assessmentState.time_remaining]);

  const GetTimeSnackbarMessage = (time: number) => {
    if (time === 300) return '5 minutes left';
    if (time === 900) return '15 minutes left';
    if (time === 1800) return '30 minutes left';
    return 'NA';
  };

  useEffect(() => {
    if (timeRemain === 300) {
      openSnackbar('warning', GetTimeSnackbarMessage(300));
    } else if (timeRemain === 900) {
      openSnackbar('warning', GetTimeSnackbarMessage(900));
    } else if (timeRemain === 1800) {
      openSnackbar('warning', GetTimeSnackbarMessage(1800));
    }
  }, [timeRemain]);

  useEffect(() => {
    if (timeRemain <= 120) {
      setTimeClass(`${classes.time} ${classes.error}`);
    }
  }, [timeRemain]);

  useEffect(() => {
    const localTimer = setInterval(() => {
      if (
        endTime &&
        endTime !== Infinity &&
        timeRemain > 0 &&
        timeRemain !== Infinity
      ) {
        const remainingTime = Math.max(
          Math.ceil((endTime - Date.now()) / 1000),
          0
        );
        dispatch(setTimeRemain(remainingTime));
      }
    }, 1000);
    return () => clearInterval(localTimer);
  }, [timeRemain, endTime]);

  const handleSubmitLog = async () => {
    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (submitInFlight.current) return;

    submitInFlight.current = true;
    let submissionSucceeded = false;
    try {
      const response = await assessmentSubmission(accessKey as string);
      if (response) {
        submissionSucceeded = true;
        dispatch(setStatus(authStatusEnum.ENDED));
        ExitFullScreen();
        sessionStorage.removeItem(tokenKey);
        const { success, message } = response.data;
        openSnackbar(success ? 'success' : 'error', message);
      }
    } catch (err) {
      console.error(err);
      openSnackbar('error', generalErrorMessage);
    } finally {
      if (!submissionSucceeded) {
        submitInFlight.current = false;
        setPause(false);
      }
    }
  };

  const handleSubmitConfirm = () => {
    setPause(true);
    dispatch(setPhotoUploadPause(true));
  };

  useEffect(() => {
    if (timeRemain < 1 && !pauseSubmit && !codeRunning) {
      handleSubmitConfirm();
    }
  }, [timeRemain, pauseSubmit, codeRunning]);

  useEffect(() => {
    let count = 0;
    const qMap = assessmentState.questions_map;
    if (qMap?.length > 0) {
      for (let i = 0; i < qMap.length; i++) {
        if (qMap[i].answered) count++;
      }
    }
    dispatch(setQuestionsAnswered(count));
  }, [assessmentState.questions_map]);

  const handleSubmitOpen = () => {
    let dataCopy = JSON.parse(JSON.stringify(assessmentState.questions_map));
    dataCopy.map((item: questionMap) => (item.visited = true));
    dispatch(setQuestionsMap([...dataCopy]));
    setOpenSubmitPopup(true);
  };

  const handleSubmitClose = () => {
    setOpenSubmitPopup(false);
  };

  useEffect(() => {
    if (pause && !assessmentState.photo_upload_pause) {
      handleSubmitLog();
    }
  }, [pause, assessmentState.photo_upload_pause]);

  return (
    <header className={classes.container}>
      <div>
        <div className={classes.assessmentTitle}>
          {assessmentState.assessment_name}
        </div>
      </div>
      <div className={classes.subContainer}>
        {videoRecording ? (
          <div className={classes.recordingContainer}>
            <RadioButtonCheckedIcon color="error" />
            <p>REC...</p>
          </div>
        ) : (
          <></>
        )}
        <div className={classes.answerd}>
          Answered:{' '}
          {`${assessmentState.question_answered}/${assessmentState.total_question}`}
        </div>
        <div className={timeClass}>
          <AccessTimeIcon color="inherit" />{' '}
          <span className={classes.timeContainer}>
            {moment.utc(timeRemain * 1000).format('HH:mm:ss')}
          </span>
        </div>
        <button
          className={classes.submitButton}
          onClick={handleSubmitOpen}
          disabled={
            videoRecording ||
            networkState.status === NetworkStatusEnum.OFFLINE ||
            codeRunning ||
            videoRecording
          }
        >
          {!isMobile ? 'Submit Assessment' : 'Submit'}
        </button>
      </div>
      <SubmitAssessmentDialog
        open={openSubmitPopup}
        handleClose={handleSubmitClose}
      />
    </header>
  );
};

export default Header;
